/**
 * Formula Types
 *
 * Defines types for the formula expression language used in custom reports.
 * Supports aggregate functions (SUM, AVG, COUNT, MIN, MAX) and arithmetic operations.
 */

// Token types for the lexer
export type FormulaTokenType =
  | 'NUMBER'
  | 'IDENTIFIER'
  | 'FUNCTION'
  | 'LPAREN'
  | 'RPAREN'
  | 'PLUS'
  | 'MINUS'
  | 'MULTIPLY'
  | 'DIVIDE'
  | 'COMMA'
  | 'EOF';

export interface FormulaToken {
  type: FormulaTokenType;
  value: string;
  position: number;
  length: number;
}

// AST Node Types
export type FormulaASTNodeType =
  | 'number'
  | 'field'
  | 'function_call'
  | 'binary_op'
  | 'unary_op';

export interface FormulaNumberNode {
  type: 'number';
  value: number;
}

export interface FormulaFieldNode {
  type: 'field';
  name: string;
}

export interface FormulaFunctionCallNode {
  type: 'function_call';
  name: string;
  argument: FormulaASTNode;
}

export interface FormulaBinaryOpNode {
  type: 'binary_op';
  operator: '+' | '-' | '*' | '/';
  left: FormulaASTNode;
  right: FormulaASTNode;
}

export interface FormulaUnaryOpNode {
  type: 'unary_op';
  operator: '-';
  operand: FormulaASTNode;
}

export type FormulaASTNode =
  | FormulaNumberNode
  | FormulaFieldNode
  | FormulaFunctionCallNode
  | FormulaBinaryOpNode
  | FormulaUnaryOpNode;

// Validation types
export interface FormulaValidationError {
  message: string;
  position: number;
  length: number;
}

export interface FormulaValidationResult {
  isValid: boolean;
  errors: FormulaValidationError[];
  warnings: string[];
  ast: FormulaASTNode | null;
  usedFields: string[];
  usedFunctions: string[];
}

// Formula definition stored in reports
export type FormulaResultType = 'currency' | 'percentage' | 'number' | 'hours';

export interface FormulaDefinition {
  expression: string;
  resultType: FormulaResultType;
}

// Available aggregate functions
export const FORMULA_FUNCTIONS = ['SUM', 'AVG', 'COUNT', 'MIN', 'MAX'] as const;
export type FormulaFunction = (typeof FORMULA_FUNCTIONS)[number];

// Function labels in Danish
export const FORMULA_FUNCTION_LABELS: Record<FormulaFunction, string> = {
  SUM: 'Sum',
  AVG: 'Gennemsnit',
  COUNT: 'Antal',
  MIN: 'Minimum',
  MAX: 'Maksimum',
};

// Function descriptions in Danish
export const FORMULA_FUNCTION_DESCRIPTIONS: Record<FormulaFunction, string> = {
  SUM: 'Summerer alle værdier',
  AVG: 'Beregner gennemsnittet',
  COUNT: 'Tæller antal poster',
  MIN: 'Finder mindste værdi',
  MAX: 'Finder største værdi',
};

// Available fields by data source
export interface FormulaFieldDefinition {
  name: string;
  label: string;
  description: string;
}

export const FORMULA_FIELDS_BY_DATA_SOURCE: Record<string, FormulaFieldDefinition[]> = {
  projects: [
    { name: 'price', label: 'Pris', description: 'Projektets aftalte pris' },
    { name: 'actualRevenue', label: 'Omsætning', description: 'Faktisk omsætning' },
    { name: 'materialCosts', label: 'Materialeomkostninger', description: 'Omkostninger til materialer' },
    { name: 'laborCosts', label: 'Lønomkostninger', description: 'Omkostninger til løn' },
    { name: 'totalCosts', label: 'Totale omkostninger', description: 'Samlede omkostninger' },
    { name: 'profit', label: 'Profit', description: 'Omsætning minus omkostninger' },
    { name: 'profitMargin', label: 'Overskudsgrad', description: 'Profit i procent af omsætning' },
    { name: 'actualHours', label: 'Timer', description: 'Registrerede timer' },
    { name: 'estimatedHours', label: 'Estimerede timer', description: 'Forventede timer' },
  ],
  time_entries: [
    { name: 'duration', label: 'Varighed', description: 'Tid i minutter' },
    { name: 'hourlyRate', label: 'Timepris', description: 'Pris pr. time' },
    { name: 'totalCost', label: 'Samlet omkostning', description: 'Timepris * varighed' },
  ],
  materials: [
    { name: 'quantity', label: 'Antal', description: 'Antal enheder' },
    { name: 'unitPrice', label: 'Enhedspris', description: 'Pris pr. enhed' },
    { name: 'totalPrice', label: 'Totalpris', description: 'Kostpris i alt' },
    { name: 'totalSalePrice', label: 'Salgspris', description: 'Salgspris i alt' },
    { name: 'appliedMarkupPercent', label: 'Avanceprocent', description: 'Anvendt avance i procent' },
  ],
  quotes: [
    { name: 'subtotal', label: 'Subtotal', description: 'Beløb før moms og rabat' },
    { name: 'discountAmount', label: 'Rabat', description: 'Rabatbeløb' },
    { name: 'vatAmount', label: 'Moms', description: 'Momsbeløb' },
    { name: 'total', label: 'Total', description: 'Samlet beløb inkl. moms' },
    { name: 'estimatedHours', label: 'Estimerede timer', description: 'Forventede timer på tilbuddet' },
    { name: 'totalHours', label: 'Timer i alt', description: 'Samlede timer registreret' },
    { name: 'totalLaborCost', label: 'Lønomkostninger', description: 'Samlede lønomkostninger' },
  ],
};

// Data source labels in Danish (for cross-source mode)
export const DATA_SOURCE_LABELS_SHORT: Record<string, string> = {
  projects: 'Projekter',
  time_entries: 'Tidsreg.',
  materials: 'Materialer',
  quotes: 'Tilbud',
};

// All data sources for cross-source mode
export const ALL_DATA_SOURCES = ['projects', 'time_entries', 'materials', 'quotes'] as const;

/**
 * Get all fields with prefixed names for cross-source mode
 * Returns fields like: projects.actualRevenue, quotes.total, etc.
 */
export function getAllPrefixedFields(): FormulaFieldDefinition[] {
  const allFields: FormulaFieldDefinition[] = [];

  for (const [source, fields] of Object.entries(FORMULA_FIELDS_BY_DATA_SOURCE)) {
    const sourceLabel = DATA_SOURCE_LABELS_SHORT[source] || source;
    for (const field of fields) {
      allFields.push({
        name: `${source}.${field.name}`,
        label: `${sourceLabel}: ${field.label}`,
        description: field.description,
      });
    }
  }

  return allFields;
}

// Result type labels
export const FORMULA_RESULT_TYPE_LABELS: Record<FormulaResultType, string> = {
  currency: 'Valuta (kr.)',
  percentage: 'Procent (%)',
  number: 'Tal',
  hours: 'Timer (t)',
};
